import React, { useState, useEffect, useRef } from 'react';

/**
 * A reusable filter dropdown component with search and multi-select functionality
 * 
 * @param {string} label - The label to display on the button
 * @param {Object} items - Object with item keys and their counts/stats
 * @param {Set} selectedItems - Set of currently selected items
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {boolean} showCount - Whether to display counts next to items (default: true)
 * @param {Function} formatCount - Optional function to format the count display
 * @param {Function} formatLabel - Optional function to format the item label
 * @param {Function} sortItems - Optional function to sort items
 */
const FilterDropdown = ({
  label = "Filter",
  items = {},
  selectedItems = new Set(),
  onSelectionChange,
  showCount = true,
  formatCount = (count) => count >= 1000 ? `${(count / 1000).toFixed(1)}K` : count.toString(),
  formatLabel = (item) => item,
  sortItems = (itemKeys, itemStats) => itemKeys.sort((a, b) => itemStats[b] - itemStats[a])
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get filtered items based on search term
  const getFilteredItems = () => {
    const allItems = Object.keys(items);
    if (!searchTerm) return allItems;
    
    return allItems.filter(item =>
      item.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Handle item selection toggle
  const handleItemToggle = (item) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(item)) {
      newSelected.delete(item);
    } else {
      newSelected.add(item);
    }
    onSelectionChange(newSelected);
  };

  // Select all items
  const handleSelectAll = () => {
    const allItems = Object.keys(items);
    onSelectionChange(new Set(allItems));
  };

  // Deselect all items
  const handleClearAll = () => {
    onSelectionChange(new Set());
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredItems = getFilteredItems();
  const sortedItems = sortItems(filteredItems, items);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className="font-medium">{label}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">{label}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSelectAll}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Select All
              </button>
              <button
                onClick={handleClearAll}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Type to search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Items List */}
          <div className="max-h-64 overflow-y-auto">
            {sortedItems.map((item) => {
              const count = items[item];
              const isSelected = selectedItems.has(item);
              const formattedCount = showCount ? formatCount(count) : null;
              
              return (
                <div
                  key={item}
                  className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleItemToggle(item)}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleItemToggle(item)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-sm font-medium">{formatLabel(item)}</span>
                  </div>
                  {showCount && <span className="text-sm text-gray-600">{formattedCount}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;

